import { useState } from 'react'
import { useParams } from 'react-router'
import Box from '@cloudscape-design/components/box'
import Spinner from '@cloudscape-design/components/spinner'
import { useClient } from '@/hooks/useClients'
import ConfigureWizard from './ConfigureWizard'
import ConfigureSummary, {
  isBusinessInfoEmpty,
  isOnlinePresenceEmpty,
  isServicesEmpty,
  isCampaignEmpty,
  isScheduleEmpty,
} from './ConfigureSummary'
import type { ClientConfig } from '@/lib/types'

type Mode = { kind: 'summary' } | { kind: 'wizard'; startStep: number }

// A brand-new client (no config at all) should land directly in the wizard.
// Any signal of existing configuration means the user should see the summary.
function isFullyUnconfigured(client: ClientConfig): boolean {
  return (
    isBusinessInfoEmpty(client) &&
    isOnlinePresenceEmpty(client) &&
    isServicesEmpty(client) &&
    isCampaignEmpty(client) &&
    isScheduleEmpty(client)
  )
}

export default function ConfigurePage() {
  const { clientId } = useParams<{ clientId: string }>()
  const { client, loading } = useClient(clientId ?? '')
  const [mode, setMode] = useState<Mode | null>(null)

  if (!clientId) return null

  if (loading && !client) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    )
  }

  // First render after data loads: decide initial mode from the client's config.
  const resolvedMode: Mode =
    mode ?? (client && isFullyUnconfigured(client)
      ? { kind: 'wizard', startStep: 0 }
      : { kind: 'summary' })

  if (resolvedMode.kind === 'wizard') {
    return (
      <ConfigureWizard
        clientId={clientId}
        startStep={resolvedMode.startStep}
        onDone={() => setMode({ kind: 'summary' })}
        onCancel={() => setMode({ kind: 'summary' })}
      />
    )
  }

  return (
    <ConfigureSummary
      clientId={clientId}
      onEditStep={(stepIndex: number) => setMode({ kind: 'wizard', startStep: stepIndex })}
    />
  )
}
